import React from "react"
import { type ConnectedProps, connect } from "react-redux"
import { Navigate } from "react-router-dom"

import { ROLE_IDS } from "../../constants/roles.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { type RootState } from "../../store"

interface PortalRouteProps extends ConnectedProps<typeof connector> {
  children: React.ReactNode
}

/**
 * Gates the customer portal route tree. Mirrors PrivateRoute's
 * authenticated check, then adds the role check the other direction:
 * only a customer account reaches these pages, everyone else is sent back
 * to the staff app rather than seeing an unauthorized state for a portal
 * that was never meant for them.
 *
 * A logged-out visit to a deep link is sent to plain `/login`, same as
 * every other protected route in this app today (`useStartup`'s route
 * guard does not preserve a destination either) — an authenticated
 * session's deep links stay intact via `useStartup`'s own `innerPages`
 * allowlist, which includes `portal`.
 */
function PortalRoute({ children, auth, userInfo }: PortalRouteProps) {
  const isAuthenticated = !!auth?.accessToken && !!userInfo

  if (!isAuthenticated) {
    return <Navigate to={RoutesPaths.auth.login} replace />
  }

  if (userInfo?.role?.id !== ROLE_IDS.CUSTOMER) {
    return <Navigate to={RoutesPaths.home} replace />
  }

  return <>{children}</>
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  userInfo: state.auth.userInfo
})

const connector = connect(mapStateToProps)
export default connector(PortalRoute)
